import { supabase } from '../lib/supabase';

export type ActivityType = 
  | 'item_added' 
  | 'item_checked'
  | 'low_stock'
  | 'memory_posted' 
  | 'comment_added' 
  | 'poll_voted' 
  | 'meal_planned'
  | 'member_joined';

export interface ActivityEvent {
  id: string;
  family_id: string;
  actor_id: string;
  event_type: ActivityType;
  content: string;
  payload: any;
  module?: string;
  created_at: string;
  // Joined profile data
  actor?: {
    display_name: string;
    avatar_url: string;
    avatar_emoji: string;
    avatar_color: string;
  };
}

export const activityService = {
  async logEvent(params: {
    familyId: string;
    actorId: string;
    type: ActivityType;
    content: string;
    payload?: any;
  }) {
    const { error } = await supabase
      .from('activity_events')
      .insert({
        family_id: params.familyId,
        actor_id: params.actorId,
        event_type: params.type,
        content: params.content,
        payload: params.payload || {}
      });

    if (error) {
      console.error('Error logging activity:', error);
    }
  },

  async fetchEvents(familyId: string, limit = 50): Promise<ActivityEvent[]> {
    const { data, error } = await supabase
      .from('activity_events')
      .select(`
        *,
        actor:profiles(display_name, avatar_url, avatar_emoji, avatar_color)
      `)
      .eq('family_id', familyId)
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) {
      console.error('Error fetching activity:', error);
      return [];
    }

    return data as ActivityEvent[];
  },

  async deleteEvent(eventId: string) {
    const { error } = await supabase
      .from('activity_events')
      .delete()
      .eq('id', eventId);
    if (error) {
      console.error('Error deleting activity event:', error);
      throw error;
    }
  },

  async clearFamilyFeed(familyId: string) {
    const { error } = await supabase
      .from('activity_events')
      .delete()
      .eq('family_id', familyId);
    if (error) {
      console.error('Error clearing family feed:', error);
      throw error;
    }
  },

  /**
   * Subscribe to realtime activity events for a family.
   * Returns an unsubscribe function — call it on component unmount.
   */
  subscribeToFamily(familyId: string, onNewEvent: (event: ActivityEvent) => void): () => void {
    const channelName = `activity-realtime-${familyId}-${Date.now()}-${Math.random().toString(36).substring(2, 6)}`;
    const channel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'activity_events',
          filter: `family_id=eq.${familyId}`,
        },
        async (payload) => {
          // Enrich the new row with actor profile data
          const row = payload.new as ActivityEvent;
          if (row.actor_id) {
            const { data: actor } = await supabase
              .from('profiles')
              .select('display_name, avatar_url, avatar_emoji, avatar_color')
              .eq('id', row.actor_id)
              .single();
            if (actor) row.actor = actor as ActivityEvent['actor'];
          }
          onNewEvent(row);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  },
};
